import { isPlatformBrowser } from '@angular/common';
import { httpResource, HttpResourceRef } from '@angular/common/http';
import { effect, inject, Injectable, Injector, PLATFORM_ID, Signal, signal, WritableSignal } from '@angular/core';

import { EcmDispatcherModule, StoreService } from '@ecm/dispatcher';

import { HttpMethod } from '../enums/http-method.enum';
import { RequestOptions } from '../enums/request-options';
import { Status } from '../enums/status.enum';
import { TransactionInputs } from '../enums/transaction-inputs';
import { RequestUtils } from '../utils/http/request.utils';
import { IdUtils } from '../utils/id.utils';
import { BcoService } from './bco.service';
import { ErrorHandlerService } from './error-handler.service';
import { RandomUrlService } from './random-url.service';
import { UaService } from './ua-service';

/**
 * 🔥 Configuration for mutation resources (POST/PUT/PATCH/DELETE).
 * Set to `undefined` to skip the request (initial state / not ready yet).
 */
export interface MutationRequest<TBody = unknown> {
    url: string;
    body?: TBody;
}

/**
 * 📦 ErableHttpResourceService — Signal-based equivalent of ErableHttpService.
 *
 * Uses Angular's `httpResource` (Angular 20+) instead of `HttpClient` for reactive data fetching.
 * Returns `HttpResourceRef<T>` (signals) instead of `Observable<T>`.
 *
 * ## Key architectural differences with ErableHttpService:
 *
 * | Concern               | ErableHttpService (Observable)         | ErableHttpResourceService (Signal)        |
 * |-----------------------|----------------------------------------|-------------------------------------------|
 * | **Return type**       | `Observable<T>`                        | `HttpResourceRef<T>` (signals)            |
 * | **State management**  | Manual (loading, error, value)         | Built-in via `status()`, `isLoading()`    |
 * | **Caching (GET)**     | Custom in-memory cache by URL          | Resource lifecycle (survives until destroyed) |
 * | **Re-fetching**       | Manual (`force` option)                | `.reload()` or request signal change      |
 * | **Mutations**         | Imperative (call method → get Observable) | Reactive (update signal → resource fires) |
 * | **Error handling**    | Observable `error` + `onError` callback | `status() === 'error'`, `error()` signal |
 * | **BCO tracking**      | Inline in request lifecycle            | Via `trackResource()` effect helper       |
 * | **Timeout**           | Per-request via `rxjs/timeout`         | ⚠️ Use HTTP interceptor instead           |
 * | **JWT processing**    | Inline `computeAdvisorJwtHeader`       | ⚠️ Use HTTP interceptor instead           |
 *
 * ## ⚠️ Cross-cutting concerns handled differently
 *
 * `httpResource` uses Angular's `HttpClient` internally, so **HTTP interceptors** are the
 * idiomatic way to handle:
 * - **Timeout**: Create a timeout interceptor using `AbortController` or `rxjs/timeout`
 * - **BCO transaction tracking**: Use `trackResource()` for resource-level tracking,
 *   or create an interceptor for request-level tracking
 * - **JWT advisor header processing**: Create an interceptor that reads response headers
 *
 * ## Usage examples
 *
 * ### 🔥 Reactive GET in a service
 * ```typescript
 * @Injectable({ providedIn: 'root' })
 * export class OrderService {
 *     private readonly _erableResource = inject(ErableHttpResourceService);
 *     private readonly _env = inject(EnvironmentService);
 *
 *     readonly ordersResource = this._erableResource.get<Order[]>(
 *         () => this._env.instant().API.COMMANDES.GET_ORDERS_URL,
 *     );
 * }
 * ```
 *
 * ### 🔥 Reactive GET with dynamic parameters
 * ```typescript
 * // Resource re-fetches automatically when contractId() changes
 * readonly contractId = input.required<string>();
 * readonly contractResource = this._erableResource.get<Contract>(
 *     () => `/api/contracts/${this.contractId()}`,
 * );
 * ```
 *
 * ### 🔥 POST mutation triggered on demand
 * ```typescript
 * private readonly _submitRequest = signal<MutationRequest | undefined>(undefined);
 * readonly submitResource = this._erableResource.post<Response>(
 *     this._submitRequest,
 * );
 *
 * onSubmit(data: FormData): void {
 *     this._submitRequest.set({ url: '/api/submit', body: data });
 * }
 * ```
 *
 * ### 🔥 BCO tracking with trackResource()
 * ```typescript
 * private readonly _tracking = this._erableResource.trackResource(this.ordersResource, {
 *     method: HttpMethod.get,
 *     feature: 'commandes',
 *     functionality: 'list',
 *     service: 'orders',
 * });
 * ```
 */
@Injectable({
    providedIn: 'root',
})
export class ErableHttpResourceService {
    private static readonly CLASS_NAME = 'ErableHttpResourceService';

    private readonly _injector = inject(Injector);
    private readonly _bcoSvc = inject(BcoService);
    private readonly _uaSvc = inject(UaService);
    private readonly _errorHandlerSvc = inject(ErrorHandlerService);
    private readonly _randomUrlSvc = inject(RandomUrlService);
    private readonly _platformId = inject(PLATFORM_ID);

    /** 🔧 Orange monitoring headers */
    private readonly ORIGIN_ID_HEADER = 'X-Orange-Origin-Id';
    private readonly CALLER_ID_HEADER = 'X-Orange-Caller-Id';
    private readonly DEVICE_TYPE_HEADER = 'X-App-Device-Type';

    constructor() {
        this._initSessionId();
    }

    // ────────────────────────────────────────────────────────────────────────
    //  📦 GET Resource
    // ────────────────────────────────────────────────────────────────────────

    /**
     * Creates a reactive GET resource.
     * The resource automatically fetches when the URL signal changes.
     * Return `undefined` from the URL function to skip the request.
     *
     * @param url Reactive function returning the URL (or `undefined` to skip)
     * @param options Optional request options (headers, partner for round-robin)
     * @returns `HttpResourceRef<T>` with `.value()`, `.status()`, `.isLoading()`, `.error()`
     */
    public get<T>(url: () => string | undefined, options?: RequestOptions): HttpResourceRef<T> {
        return httpResource<T>(
            () => {
                const rawUrl = url();
                if (!rawUrl) return undefined;

                const { url: processedUrl } = this._processUrl(rawUrl, options?.partner);

                return {
                    url: processedUrl,
                    method: 'GET',
                    headers: this._buildHeaders(options),
                    withCredentials: true,
                    reportProgress: false,
                };
            },
            { injector: this._injector },
        );
    }

    // ────────────────────────────────────────────────────────────────────────
    //  📦 POST Resource
    // ────────────────────────────────────────────────────────────────────────

    /**
     * Creates a reactive POST resource.
     * The resource fires when the request signal emits a non-undefined value.
     *
     * @param request Reactive function (or signal) returning `{ url, body }` or `undefined`
     * @param options Optional request options
     */
    public post<TResponse, TBody = unknown>(
        request: Signal<MutationRequest<TBody> | undefined> | (() => MutationRequest<TBody> | undefined),
        options?: RequestOptions,
    ): HttpResourceRef<TResponse> {
        return this._createMutationResource<TResponse, TBody>('POST', request, options);
    }

    // ────────────────────────────────────────────────────────────────────────
    //  📦 PUT Resource
    // ────────────────────────────────────────────────────────────────────────

    /**
     * Creates a reactive PUT resource.
     *
     * @param request Reactive function (or signal) returning `{ url, body }` or `undefined`
     * @param options Optional request options
     */
    public put<TResponse, TBody = unknown>(
        request: Signal<MutationRequest<TBody> | undefined> | (() => MutationRequest<TBody> | undefined),
        options?: RequestOptions,
    ): HttpResourceRef<TResponse> {
        return this._createMutationResource<TResponse, TBody>('PUT', request, options);
    }

    // ────────────────────────────────────────────────────────────────────────
    //  📦 PATCH Resource
    // ────────────────────────────────────────────────────────────────────────

    /**
     * Creates a reactive PATCH resource.
     *
     * @param request Reactive function (or signal) returning `{ url, body }` or `undefined`
     * @param options Optional request options
     */
    public patch<TResponse, TBody = unknown>(
        request: Signal<MutationRequest<TBody> | undefined> | (() => MutationRequest<TBody> | undefined),
        options?: RequestOptions,
    ): HttpResourceRef<TResponse> {
        return this._createMutationResource<TResponse, TBody>('PATCH', request, options);
    }

    // ────────────────────────────────────────────────────────────────────────
    //  📦 DELETE Resource
    // ────────────────────────────────────────────────────────────────────────

    /**
     * Creates a reactive DELETE resource.
     *
     * @param url Reactive function returning the URL (or `undefined` to skip)
     * @param options Optional request options
     */
    public delete<T>(url: () => string | undefined, options?: RequestOptions): HttpResourceRef<T> {
        return httpResource<T>(
            () => {
                const rawUrl = url();
                if (!rawUrl) return undefined;

                const { url: processedUrl } = this._processUrl(rawUrl, options?.partner);

                return {
                    url: processedUrl,
                    method: 'DELETE',
                    headers: this._buildHeaders(options),
                    withCredentials: true,
                    reportProgress: false,
                };
            },
            { injector: this._injector },
        );
    }

    // ────────────────────────────────────────────────────────────────────────
    //  🔔 BCO Tracking Helper
    // ────────────────────────────────────────────────────────────────────────

    /**
     * Creates an effect that tracks a resource's lifecycle via BCO (begin/end transaction).
     * Place this alongside the resource in your service or component.
     *
     * The effect watches the resource `status()` signal and:
     * - Starts a BCO transaction when the resource begins loading
     * - Ends the transaction with OK when resolved
     * - Ends the transaction with KOSI/KORG/TO when errored
     *
     * @param resource The `HttpResourceRef` to track
     * @param trackingOptions BCO metadata (feature, functionality, service, etc.)
     * @returns A writable signal holding the current transactionId (for external use if needed)
     *
     * @example
     * ```typescript
     * readonly ordersResource = this._erableResource.get<Order[]>(() => url);
     * private readonly _tracking = this._erableResource.trackResource(this.ordersResource, {
     *     method: HttpMethod.get,
     *     feature: 'commandes',
     *     functionality: 'list',
     *     service: 'orders',
     * });
     * ```
     */
    public trackResource<T>(
        resource: HttpResourceRef<T>,
        trackingOptions: {
            method: HttpMethod;
            feature?: string;
            functionality?: string;
            pageId?: string;
            partner?: string;
            service?: string;
        },
    ): WritableSignal<string | null> {
        const transactionId: WritableSignal<string | null> = signal(null);

        effect(
            () => {
                const status = resource.status();

                if (status === 'loading' || status === 'reloading') {
                    // 🔥 Begin BCO transaction when resource starts loading
                    const inputs: TransactionInputs = {
                        httpMethod: trackingOptions.method,
                        feature: trackingOptions.feature ?? 'undefined',
                        functionality: trackingOptions.functionality ?? 'undefined',
                        pageId: trackingOptions.pageId ?? 'undefined',
                        partner: trackingOptions.partner ?? 'undefined',
                        service: trackingOptions.service ?? 'undefined',
                        originId: RequestUtils.getOriginId(),
                        consumerId: RequestUtils.getCallerId(),
                        sessionId: StoreService.get('session')?.id ?? '',
                    };

                    transactionId.set(this._bcoSvc.beginTransaction(inputs));
                }

                if (status === 'resolved') {
                    const txId = transactionId();
                    if (txId) {
                        this._bcoSvc.endTransaction(txId, Status.OK, '200', '');
                        transactionId.set(null);
                    }
                }

                if (status === 'error') {
                    const txId = transactionId();
                    const error = resource.error();

                    if (txId) {
                        const { errorStatus, errorCode, errorMessage } = this._classifyError(error);
                        this._bcoSvc.endTransaction(txId, errorStatus, errorCode, errorMessage);
                        transactionId.set(null);
                    }
                }
            },
            { injector: this._injector },
        );

        return transactionId;
    }

    // ────────────────────────────────────────────────────────────────────────
    //  🔧 Private helpers
    // ────────────────────────────────────────────────────────────────────────

    /**
     * 🔧 Creates a mutation resource (POST/PUT/PATCH) with common configuration.
     */
    private _createMutationResource<TResponse, TBody = unknown>(
        method: 'POST' | 'PUT' | 'PATCH',
        request: Signal<MutationRequest<TBody> | undefined> | (() => MutationRequest<TBody> | undefined),
        options?: RequestOptions,
    ): HttpResourceRef<TResponse> {
        return httpResource<TResponse>(
            () => {
                const req = request();
                if (!req) return undefined;

                const { url: processedUrl } = this._processUrl(req.url, options?.partner);

                return {
                    url: processedUrl,
                    method,
                    body: req.body,
                    headers: this._buildHeaders(options),
                    withCredentials: true,
                    reportProgress: false,
                };
            },
            { injector: this._injector },
        );
    }

    /**
     * 🔧 Builds default headers matching ErableHttpService conventions.
     * Includes Orange monitoring headers + custom headers from options.
     */
    private _buildHeaders(options?: RequestOptions): Record<string, string> {
        const headers: Record<string, string> = {
            [this.ORIGIN_ID_HEADER]: RequestUtils.getOriginId(),
            [this.CALLER_ID_HEADER]: RequestUtils.getCallerId(),
            [this.DEVICE_TYPE_HEADER]: this._uaSvc.getDeviceType(),
            Accept: 'application/json',
            'Cache-Control': 'no-cache, no-store',
            'X-Orange-Session-Id': StoreService.get('session')?.id ?? '',
            'X-Orange-Request-Id': IdUtils.generateUUID(),
        };

        // Add Content-Type only if not explicitly disabled
        if (!options?.disableContentTypeHeader) {
            headers['Content-Type'] = 'application/json';
        }

        // Merge custom headers from options
        if (options?.headers) {
            for (const key of options.headers.keys()) {
                headers[key] = options.headers.get(key);
            }
        }

        return headers;
    }

    /**
     * 🔧 Processes URL through RandomUrlService for round-robin support.
     */
    private _processUrl(url: string, partner?: string): { url: string; partner: string } {
        return this._randomUrlSvc.getUrlAndPartner(url, partner ?? 'undefined');
    }

    /**
     * 🔧 Classifies an HTTP error into BCO-compatible status/code/message.
     * Mirrors the error classification logic from ErableHttpService.onError.
     */
    private _classifyError(error: unknown): { errorStatus: string; errorCode: string; errorMessage: string } {
        // HttpErrorResponse from Angular's HttpClient (used internally by httpResource)
        if (error && typeof error === 'object' && 'status' in error) {
            const httpError = error as { status?: number; name?: string; message?: string; error?: any };
            const isTimeout = httpError.name === 'TimeoutError';

            let errorCode = isTimeout ? 'ECM_TO' : httpError.status?.toString();
            let errorStatus: string;

            if (isTimeout) {
                errorStatus = Status.TO;
            } else if (errorCode === undefined || errorCode === 'undefined') {
                this._errorHandlerSvc.handleError(error as unknown as Error);
                errorCode = `ECM_${httpError.name}`;
                errorStatus = Status.KORG;
            } else {
                errorStatus = errorCode.startsWith('4') ? Status.KORG : Status.KOSI;
            }

            const errorMessage =
                httpError.error?.error?.technicalDescription ??
                httpError.error?.error?.technicalMessage ??
                httpError.message ??
                'undefined';

            return { errorStatus, errorCode, errorMessage };
        }

        // Fallback for unknown errors
        return {
            errorStatus: Status.KOSI,
            errorCode: 'ECM_UNKNOWN',
            errorMessage: error instanceof Error ? error.message : 'Unknown error',
        };
    }

    /**
     * 🔧 Initializes session ID in StoreService (same as ErableHttpService).
     */
    private _initSessionId(): void {
        if (isPlatformBrowser(this._platformId)) {
            const sessionId = IdUtils.generateUUID();
            EcmDispatcherModule.sendConsoleInfo(ErableHttpResourceService.CLASS_NAME, 'Storing Session ' + sessionId);
            StoreService.set('session', { id: sessionId });
        }
    }
}
